import { useEffect, useState } from "react";
import { Building2, Copy, Check, Share2, Loader2, AtSign, ExternalLink, X, Smartphone } from "lucide-react";
import api, { fmtNaira } from "@/lib/api";
import { toast } from "sonner";
import { shareToWhatsApp } from "@/lib/share";

export default function PaymentHub({ onClose }) {
  const [tab, setTab] = useState("dva"); // dva | cashtag | link | whatsapp

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl max-h-[92dvh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="payment-hub">
        <div className="sticky top-0 bg-white border-b border-border/60 p-4 flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold">Receive money</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-accent flex items-center justify-center" data-testid="close-hub"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex gap-1 p-3 overflow-x-auto no-scrollbar border-b border-border/40">
          <Tab active={tab === "dva"} onClick={() => setTab("dva")} icon={Building2} label="Bank" testid="tab-dva" />
          <Tab active={tab === "cashtag"} onClick={() => setTab("cashtag")} icon={AtSign} label="Cashtag" testid="tab-cashtag" />
          <Tab active={tab === "link"} onClick={() => setTab("link")} icon={ExternalLink} label="Link/QR" testid="tab-link" />
          <Tab active={tab === "whatsapp"} onClick={() => setTab("whatsapp")} icon={Smartphone} label="WhatsApp" testid="tab-whatsapp" />
        </div>
        <div className="p-5">
          {tab === "dva" && <DVAPanel />}
          {tab === "cashtag" && <CashtagPanel />}
          {tab === "link" && <LinkPanel />}
          {tab === "whatsapp" && <WhatsAppPanel />}
        </div>
      </div>
    </div>
  );
}

function Tab({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button onClick={onClick} className={`shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition ${active ? "bg-kudi-green text-white" : "bg-accent text-kudi-ink/70"}`} data-testid={testid}>
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}

function DVAPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [copied, setCopied] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/payments/virtual-account/me");
      setData(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    setCreating(true);
    try {
      await api.post("/payments/virtual-account/create");
      await load();
      toast.success("Your Kobolog bank account is ready!");
    } catch (e) {
      toast.error(e.response?.data?.detail?.slice(0, 100) || "Failed to create account");
    } finally { setCreating(false); }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/payments/sync-dva");
      const { credited, skipped } = r.data;
      if (credited > 0) {
        toast.success(`✅ Recovered ${credited} transfer${credited > 1 ? "s" : ""}! Refreshing your wallet...`);
        setTimeout(() => window.location.reload(), 1500);
      } else {
        toast.info("All caught up — no missed transfers found.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail?.slice(0, 100) || "Sync failed");
    } finally { setSyncing(false); }
  };

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      toast.success("Copied!");
      setTimeout(() => setCopied(""), 1500);
    } catch (e) {
      console.warn("Clipboard copy failed:", e);
      toast.error("Couldn't copy — try selecting manually");
    }
  };

  if (loading) return <div className="text-center py-8 text-kudi-ink/50">Loading...</div>;

  if (!data?.has_dva) {
    return (
      <div className="text-center py-6">
        <div className="w-16 h-16 rounded-2xl bg-accent text-kudi-green flex items-center justify-center mx-auto mb-3">
          <Building2 className="w-7 h-7" />
        </div>
        <div className="font-heading text-lg font-bold">Get your own bank account</div>
        <p className="text-sm text-kudi-ink/65 mt-2 mb-5 px-4">Customers can transfer from any Nigerian bank app — like sending money to a friend. Funds auto-log as sales.</p>
        <button onClick={create} disabled={creating} className="bg-kudi-green text-white rounded-full px-6 py-3 font-semibold hover:bg-kudi-green-dark transition disabled:opacity-50" data-testid="create-dva-btn">
          {creating ? <><Loader2 className="w-4 h-4 inline animate-spin mr-1" /> Creating...</> : "Create my account"}
        </button>
      </div>
    );
  }

  const dva = data.dva;
  return (
    <div className="space-y-3" data-testid="dva-info">
      <div className="rounded-2xl bg-gradient-to-br from-kudi-green to-kudi-green-dark text-white p-5 relative overflow-hidden">
        <div className="absolute inset-0 ankara-watermark opacity-10" aria-hidden />
        <div className="relative">
          <div className="text-xs uppercase tracking-wider text-white/75 font-semibold">Your Kobolog bank account</div>
          <div className="mt-3 space-y-3">
            <Field label="Account number" value={dva.account_number} onCopy={() => copy(dva.account_number, "acc")} copied={copied === "acc"} />
            <Field label="Bank" value={dva.bank_name} />
            <Field label="Account name" value={dva.account_name} onCopy={() => copy(dva.account_name, "name")} copied={copied === "name"} />
          </div>
        </div>
      </div>
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900">
        💡 Share this with customers like you&apos;d share any bank account. When they transfer, the money lands in your wallet AND we auto-log it as a sale.
      </div>
      <button
        onClick={syncNow}
        disabled={syncing}
        className="w-full text-xs text-kudi-green hover:text-kudi-green-dark font-medium py-2 disabled:opacity-50"
        data-testid="sync-dva-btn"
      >
        {syncing ? "Checking..." : "🔄 Missing a transfer? Sync now"}
      </button>
    </div>
  );
}

function Field({ label, value, onCopy, copied }) {
  return (
    <div className="bg-white/10 backdrop-blur rounded-xl p-3 border border-white/15">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-white/70">{label}</div>
          <div className="font-heading text-lg font-bold tabular-nums mt-0.5">{value}</div>
        </div>
        {onCopy && (
          <button onClick={onCopy} className="bg-white/20 hover:bg-white/30 rounded-lg p-2 transition">
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </button>
        )}
      </div>
    </div>
  );
}

function CashtagPanel() {
  const [tag, setTag] = useState("");
  const [current, setCurrent] = useState(null);
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/me").then((r) => { if (r.data.cashtag) { setCurrent(r.data.cashtag); setTag(r.data.cashtag); } });
  }, []);

  useEffect(() => {
    if (!tag || tag === current) { setCheck(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.post("/cashtag/check", { cashtag: tag });
        setCheck(r.data);
      } catch (e) {
        // Network/validation hiccup — clear state so the user can retry typing
        setCheck(null);
        console.debug("Cashtag check failed:", e?.message);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [tag, current]);

  const claim = async () => {
    setBusy(true);
    try {
      const r = await api.post("/cashtag/claim", { cashtag: tag });
      setCurrent(r.data.cashtag);
      toast.success(`@${r.data.cashtag} is now yours! 🎉`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const share = async () => {
    const link = `${window.location.origin}/pay/${current}`;
    const text = `Pay me on Kobolog AI 👉 @${current}\n${link}`;
    const r = await shareToWhatsApp({ text, title: "My Cashtag" });
    if (r.via === "clipboard") toast.success("Link copied — paste it in WhatsApp 📋");
    else if (!r.ok && r.via !== "cancelled") toast.error("Couldn't open WhatsApp");
  };

  return (
    <div className="space-y-3" data-testid="cashtag-panel">
      <div className="bg-accent rounded-2xl p-4">
        <div className="text-xs font-semibold text-kudi-ink/70 uppercase tracking-wider">Your Kobolog Cashtag</div>
        <div className="mt-2 flex items-center gap-2">
          <span className="text-kudi-green font-heading font-bold text-2xl">@</span>
          <input
            type="text" value={tag}
            onChange={(e) => setTag(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))}
            placeholder="mama_ngozi" maxLength={20}
            className="flex-1 bg-white border border-border rounded-xl px-3 py-2 font-mono text-lg focus:outline-none focus:ring-2 focus:ring-kudi-green/20"
            data-testid="cashtag-input"
          />
        </div>
        {check && (
          <div className={`mt-2 text-xs ${check.available ? "text-emerald-700" : "text-rose-700"}`} data-testid="cashtag-status">
            {check.available ? "✓ Available!" : `✗ ${check.reason}`}
          </div>
        )}
      </div>
      {current ? (
        <button onClick={share} className="w-full bg-emerald-600 text-white rounded-full py-3 font-semibold flex items-center justify-center gap-2 hover:bg-emerald-700 transition" data-testid="share-cashtag-btn">
          <Share2 className="w-4 h-4" /> Share @{current} on WhatsApp
        </button>
      ) : (
        <button onClick={claim} disabled={!check?.available || busy} className="w-full bg-kudi-green text-white rounded-full py-3 font-semibold hover:bg-kudi-green-dark transition disabled:opacity-40" data-testid="claim-cashtag-btn">
          {busy ? "Claiming..." : "Claim my cashtag"}
        </button>
      )}
      <p className="text-xs text-kudi-ink/55 text-center">Anyone with your cashtag can pay you instantly via card or bank.</p>
    </div>
  );
}

function LinkPanel() {
  const [form, setForm] = useState({ amount: "", description: "" });
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (!form.amount) { toast.error("Enter amount"); return; }
    setBusy(true);
    try {
      const r = await api.post("/payments/create-link", {
        amount: Number(form.amount), description: form.description,
      });
      setLink(r.data);
    } catch { toast.error("Failed"); } finally { setBusy(false); }
  };

  if (link) {
    return (
      <div className="space-y-3">
        <div className="bg-white border border-border rounded-2xl p-3 flex justify-center">
          <img src={`https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(link.authorization_url)}&size=200x200&margin=10&color=115E3E`} className="w-40 h-40" alt="QR" />
        </div>
        <a href={link.authorization_url} target="_blank" rel="noreferrer" className="block text-center text-sm text-kudi-green font-semibold underline">Open checkout →</a>
        <button onClick={() => setLink(null)} className="w-full text-sm text-kudi-ink/60 py-2">Generate another</button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="Amount (₦)" className="w-full bg-accent rounded-xl px-3 py-3 text-lg font-heading font-bold focus:outline-none focus:ring-2 focus:ring-kudi-green/20" />
      <input type="text" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What for? (optional)" className="w-full bg-accent rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-kudi-green/20" />
      <button onClick={create} disabled={busy} className="w-full bg-kudi-green text-white rounded-full py-3 font-semibold hover:bg-kudi-green-dark transition disabled:opacity-50">
        {busy ? "Creating..." : "Generate link & QR"}
      </button>
      <p className="text-xs text-kudi-ink/55 text-center">Customer can pay via card, bank transfer, OR USSD code shown at checkout.</p>
    </div>
  );
}

function WhatsAppPanel() {
  const [amount, setAmount] = useState("");
  const [customer, setCustomer] = useState("");
  const [dva, setDva] = useState(null);

  useEffect(() => {
    api.get("/payments/virtual-account/me").then((r) => setDva(r.data.dva));
  }, []);

  const send = async () => {
    if (!amount) { toast.error("Enter amount"); return; }
    let link = null;
    try {
      const r = await api.post("/payments/create-link", { amount: Number(amount), customer_name: customer, description: `Payment from ${customer || "customer"}` });
      link = r.data.authorization_url;
    } catch (e) {
      console.warn("Couldn't generate payment link:", e?.response?.data?.detail || e?.message);
    }

    const lines = [
      `Hi${customer ? ` ${customer}` : ""}, please pay ${fmtNaira(Number(amount))}.`,
      "",
    ];
    if (dva) {
      lines.push(`💳 Transfer to: ${dva.bank_name} · ${dva.account_number} · ${dva.account_name}`);
      lines.push("");
    }
    if (link) lines.push(`Or pay by card: ${link}`);
    lines.push("");
    lines.push("Thanks 🙏 — sent via Kobolog AI");
    const text = lines.join("\n");
    const res = await shareToWhatsApp({ text, title: "Payment request" });
    if (res.via === "clipboard") toast.success("Message copied — paste in WhatsApp 📋");
    else if (!res.ok && res.via !== "cancelled") toast.error("Couldn't open WhatsApp");
  };

  return (
    <div className="space-y-3" data-testid="whatsapp-panel">
      <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount (₦)" className="w-full bg-accent rounded-xl px-3 py-3 text-lg font-heading font-bold focus:outline-none focus:ring-2 focus:ring-kudi-green/20" data-testid="wa-amount" />
      <input type="text" value={customer} onChange={(e) => setCustomer(e.target.value)} placeholder="Customer name (optional)" className="w-full bg-accent rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-kudi-green/20" />
      <button onClick={send} className="w-full bg-emerald-600 text-white rounded-full py-3 font-semibold flex items-center justify-center gap-2 hover:bg-emerald-700 transition" data-testid="wa-send-btn">
        <Smartphone className="w-4 h-4" /> Send via WhatsApp
      </button>
      <p className="text-xs text-kudi-ink/55 text-center">Opens WhatsApp with your bank details + payment link pre-filled.</p>
    </div>
  );
}
